import React, { useState, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, TablePagination, CircularProgress,
  Typography, Box, Button, InputAdornment,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  Search as SearchIcon,
  FilterAltOff as ClearIcon,
} from "@mui/icons-material";
import { useInventoryContext, InventoryProvider } from "../../contexts/alfaroojfresh/InventoryContext";
// ─────────────────────────────────────────
// Theme  (matches PurchaseOrders light-mode)
// ─────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f8fafc", paper: "#ffffff" },
    primary: { main: "#3753b3" },
    text: { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: {},
});
// ─────────────────────────────────────────
// Animations
// ─────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
// ─────────────────────────────────────────
// Styled components  (same palette as PurchaseOrders)
// ─────────────────────────────────────────
const Root = styled("div")({
  minHeight: "100vh",
  background: "#f8fafc",
  padding: "40px 48px",
  animation: `${fadeUp} 500ms ease both`,
});
const TitleBlock = styled("div")({
  "& .eyebrow": {
    fontSize: "0.65rem",
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "#3753b3",
    marginBottom: 6,
  },
  "& h1": {
    margin: 0,
    fontSize: "2rem",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  "& .sub": { fontSize: "0.75rem", color: "#94a3b8", marginTop: 6 },
});
const ActionBtn = styled(Button)({
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: 8,
  padding: "8px 16px",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  background: "#ffffff",
  transition: "all 0.2s",
  "&:hover": {
    borderColor: "#3753b3",
    color: "#3753b3",
    background: "rgba(245,158,11,0.05)",
  },
});
const StatCard = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "14px 18px",
  minWidth: 110,
  "& .val": {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1,
  },
  "& .lbl": {
    fontSize: "0.62rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginTop: 4,
  },
});
const FilterBar = styled("div")({
  display: "flex",
  gap: 12,
  marginBottom: 20,
  alignItems: "center",
  flexWrap: "wrap",
});
const SectionBox = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: "24px 28px",
  marginBottom: 20,
  boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
});
const SectionTitle = styled("div")({
  fontSize: "0.85rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "& .accent": { color: "#3753b3" },
});
const StyledTableContainer = styled(Paper)({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
  overflow: "hidden",
});
const tableHeadSx = {
  "& .MuiTableCell-head": {
    fontSize: "0.65rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "#94a3b8",
    background: "#f8fafc",
    fontWeight: 600,
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const tableBodySx = {
  "& .MuiTableRow-root": {
    transition: "background 0.15s",
    "&:hover": { background: "#fefce8" },
  },
  "& .MuiTableCell-body": {
    fontSize: "0.78rem",
    color: "#475569",
    padding: "12px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.78rem",
    background: "#ffffff",
    borderRadius: 2,
    color: "#0f172a",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#3753b3" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", color: "#94a3b8" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3753b3" },
};
// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────
const InventoryTable = () => {
  const { inventoryContext, loading, error } = useInventoryContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // IDs to exclude (disabled stores: Warehouse, Central Kitchen, LIWA, Catering, Kiosk)
const EXCLUDED_STORE_IDS = new Set([
  "60a8edb84fa7cd28f240661e", // Al Farooj Fresh - Warehouse
  "60a8eeb341bb70768c66ee3c", // Al Farooj Fresh - Central Kitchen
  "6145986916e3af4b7e7a5785", // Al Farooj Fresh - LIWA
  "619e1f0f19ee3b40ed5119a8", // Al Farooj Fresh - Catering ( Al Wasl Club )
  "6204d056fa0f3b04ce24cb3a", // Al Farooj Fresh - Kiosk
]);
  // Transform object -> array
  const formattedInventory = useMemo(() => {
  if (!inventoryContext) return [];
  return Object.entries(inventoryContext)
    .filter(([key]) => key !== "groupedParams")
    .filter(([, data]) => !EXCLUDED_STORE_IDS.has(data?.store_id))  // 👈 add this
    .map(([storeName, data]) => ({
      storeName,
      ...data,
    }));
}, [inventoryContext]);
  // Search
  const filteredData = useMemo(() => {
    return formattedInventory.filter((item) =>
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [formattedInventory, searchTerm]);
  // Summary totals
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, row) => {
        acc.totalSales += Number(row.total_sales || 0);
        acc.online += Number(row.online_total || 0);
        acc.dineIn += Number(row.dinein_total || 0);
        acc.takeaway += Number(row.takeaway_total || 0);
        acc.carhop += Number(row.carhop_total || 0);
        acc.walkin += Number(row.walkin_total || 0);
        acc.wastage += Number(row.wastage_cost_collected || 0);
        return acc;
      },
      { totalSales: 0, online: 0, dineIn: 0, takeaway: 0, carhop: 0, walkin: 0, wastage: 0 }
    );
  }, [filteredData]);
  // Loading
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <CircularProgress sx={{ color: "#3753b3" }} />
      </Box>
    );
  }
  // Error
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <Typography sx={{ color: "#ef4444" }}>
          Error: {String(error)}
        </Typography>
      </Box>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Root>
        {/* ── Page header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <TitleBlock>
            <div className="eyebrow">Operations</div>
            <h1>Inventory Summary</h1>
            <div className="sub">
              Overview of sales, channels, wastage, and stock across all stores.
            </div>
          </TitleBlock>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <StatCard>
              <div className="val">{filteredData.length}</div>
              <div className="lbl">Stores</div>
            </StatCard>
            <StatCard>
              <div className="val">AED {totals.totalSales.toFixed(0)}</div>
              <div className="lbl">Total Sales</div>
            </StatCard>
          </Box>
        </Box>
        {/* ── Search Filter ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Search
          </SectionTitle>
          <FilterBar>
            <TextField
              placeholder="Search store…"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, flex: 1, minWidth: 280 }}
            />
            {searchTerm && (
              <ActionBtn
                startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                onClick={() => {
                  setSearchTerm("");
                  setPage(0);
                }}
                sx={{
                  borderColor: "#fecaca",
                  color: "#ef4444",
                  "&:hover": {
                    borderColor: "#ef4444",
                    background: "rgba(239,68,68,0.05)",
                  },
                }}
              >
                Clear
              </ActionBtn>
            )}
          </FilterBar>
        </SectionBox>
        {/* ── Data Table ── */}
        <SectionBox>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <SectionTitle sx={{ mb: 0 }}>
              <span className="accent">◈</span> Store Data
              <span
                style={{
                  color: "#94a3b8",
                  fontWeight: 400,
                  fontSize: "0.72rem",
                }}
              >
                — {filteredData.length} stores
              </span>
            </SectionTitle>
            <Box display="flex" gap={3} flexWrap="wrap">
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Online:{" "}
                <strong style={{ color: "#0f172a" }}>
                  AED {totals.online.toFixed(2)}
                </strong>
              </span>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Dine-In:{" "}
                <strong style={{ color: "#0f172a" }}>
                  AED {totals.dineIn.toFixed(2)}
                </strong>
              </span>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Wastage:{" "}
                <strong style={{ color: "#ef4444" }}>
                  AED {totals.wastage.toFixed(2)}
                </strong>
              </span>
            </Box>
          </Box>
          <TableContainer component={StyledTableContainer}>
            <Table sx={{ ...tableHeadSx, ...tableBodySx }}>
              <TableHead>
                <TableRow>
                  <TableCell>Store</TableCell>
                  <TableCell align="right">Total Sales</TableCell>
                  <TableCell align="right">Online</TableCell>
                  <TableCell align="right">Dine-In</TableCell>
                  <TableCell align="right">Takeaway</TableCell>
                  <TableCell align="right">Carhop</TableCell>
                  <TableCell align="right">Walk-in</TableCell>
                  <TableCell align="right">Wastage Cost</TableCell>
                  <TableCell align="right">Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <span style={{ color: "#0f172a", fontWeight: 600 }}>
                          {row.storeName}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={{ color: "#3753b3", fontWeight: 600 }}>
                          {row.total_sales?.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        {row.online_total?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {row.dinein_total?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {row.takeaway_total?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {row.carhop_total?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {row.walkin_total?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <span style={{ color: "#ef4444", fontWeight: 600 }}>
                          {row.wastage_cost_collected?.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>
                          {row.stock_on_hand}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{
                        color: "#94a3b8 !important",
                        padding: "36px !important",
                        fontStyle: "italic",
                      }}
                    >
                      {searchTerm
                        ? "No stores match your search."
                        : "No inventory data available."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              color: "#94a3b8",
              fontSize: "0.72rem",
              "& .MuiSelect-icon": { color: "#94a3b8" },
              "& .MuiIconButton-root": {
                color: "#94a3b8",
                "&:hover": { color: "#3753b3" },
              },
              "& .Mui-disabled": { color: "#e2e8f0 !important" },
            }}
          />
        </SectionBox>
      </Root>
    </ThemeProvider>
  );
};
// ── Wrapper ──
const InventoryPage = () => (
  <InventoryProvider>
    <InventoryTable />
  </InventoryProvider>
);
export default InventoryPage;